import { useMemo } from 'react'
import { useTaskLabels } from '@/lib/db/rxdb/hooks'

/**
 * Returns a Map of labelId → distinct non-deleted task count for the given
 * workspace. Used by the /tags index page to show per-tag task counts.
 *
 * v1 counts every distinct taskId that has a non-deleted taskLabel row; it
 * does not exclude archived or closed tasks. Tighten later if needed.
 */
export function useLabelTaskCounts(workspaceId: string | null | undefined) {
  const { taskLabels, isLoading } = useTaskLabels(
    workspaceId ? { envId: workspaceId, isDeleted: false } : undefined,
  )

  const countsByLabelId = useMemo(() => {
    const buckets = new Map<string, Set<string>>()
    for (const tl of taskLabels) {
      if (tl.isDeleted) continue
      let bucket = buckets.get(tl.labelId)
      if (!bucket) {
        bucket = new Set()
        buckets.set(tl.labelId, bucket)
      }
      bucket.add(tl.taskId)
    }
    const counts = new Map<string, number>()
    for (const [labelId, set] of buckets) {
      counts.set(labelId, set.size)
    }
    return counts
  }, [taskLabels])

  return { countsByLabelId, isLoading }
}
